/**
 * Safe metric resolution for navigation transitions
 *
 * Prevents crashes when navigating between players of different position types
 * (outfield vs goalkeeper). During navigation, React may batch state updates
 * such that `isGoalkeeper` reflects the new player but `selectedMetric` still
 * holds the previous player's value, causing config lookup mismatches.
 */

import {
  DEFAULT_GK_INDIVIDUAL_METRIC,
  DEFAULT_INDIVIDUAL_METRIC,
  GK_INDIVIDUAL_METRIC_CONFIGS,
  INDIVIDUAL_METRIC_CONFIGS,
  type GkIndividualMetricType,
  type IndividualMetricType,
} from "@/features/player-detail/constants";

/**
 * Validates selectedMetric against the current config and falls back to the
 * default when mismatched. Prevents crashes during navigation between players
 * of different position types (outfield vs goalkeeper) where React batches
 * the isGoalkeeper update but selectedMetric still holds the previous value.
 * @param selectedMetric - Currently selected metric (may be stale)
 * @param isGoalkeeper - Whether the current player is a goalkeeper
 * @returns A metric key guaranteed to exist in the active config
 */
export const resolveSafeMetric = (
  selectedMetric: IndividualMetricType | GkIndividualMetricType,
  isGoalkeeper: boolean
): IndividualMetricType | GkIndividualMetricType =>
  isGoalkeeper
    ? selectedMetric in GK_INDIVIDUAL_METRIC_CONFIGS
      ? selectedMetric
      : DEFAULT_GK_INDIVIDUAL_METRIC
    : selectedMetric in INDIVIDUAL_METRIC_CONFIGS
      ? selectedMetric
      : DEFAULT_INDIVIDUAL_METRIC;
